import React, { useEffect, useState } from "react"
import EmptyState from "../../../newComponents/emptystate/index";
import analyticsIcon from "../../../assets/analyticsIcon.svg";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import TimeRangeDropdown from "../../time-range-filter/time-range-dropdown"
import "./expense-distribution.css"
import SimpleSpinner from "../../../components/loader"
import UtilHelper from "../../../utils/UtilHelper";
import { GetImages } from "../../../utils/GetImages";
import { TextCss } from "../../../components/GlobalCss/TextCss";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";

interface ExpenseCategory {
  name: string
  amount: number
  color: string
  formattedAmount?: string;
  formatted?: {
    Amount: string;
    AmountRaw: string;
    ExpenseType?: string;
  };
}

interface ExpenseDistributionProps {
  categories: ExpenseCategory[]
  heading: string
  totalAmount?: number
  timeRanges?: string[]
  defaultTimeRange?: string
  setCat_MonthValue?: React.Dispatch<React.SetStateAction<string>>
  isLoading?: boolean;
  showTotalAmountLabel?: boolean
  showCurrencySymbol?: boolean
}




const CustomTooltip = ({
  active,
  payload,
  showCurrencySymbol = true,
  effectiveSymbol = "$",
}: {
  active?: boolean;
  payload?: any;
  showCurrencySymbol?: boolean;
  effectiveSymbol?: string;
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const baseValue = data.formattedAmount || new Intl.NumberFormat("en-US", {
          style: "decimal",
          minimumFractionDigits: showCurrencySymbol ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(data.amount);

    const result = showCurrencySymbol 
      ? (baseValue.includes(effectiveSymbol) ? baseValue : `${effectiveSymbol}${baseValue}`)
      : baseValue;

    return (
      <div className="chart-tooltip">
        <div className="tooltip-content">
          <span style={{ color: data.color, fontWeight: 'bold' }}>{data.name}: </span>
          {result}
        </div>
        <div className="tooltip-arrow"></div>
      </div>
    );


  }

  return null;
};




const ExpenseDistribution: React.FC<ExpenseDistributionProps> = ({
  categories,
  heading = "Expense Distribution",
  totalAmount: propsTotalAmount,
  timeRanges = ["Last 12 Month", "Last 6 Month", "Last 3 Month", "This Month"],
  defaultTimeRange = "Last 12 Month",
  setCat_MonthValue,
  isLoading = false,
  showTotalAmountLabel = false,
  showCurrencySymbol = true,
}) => {
  const currencyFromHook = useCurrencySymbol();
  const effectiveSymbol = showCurrencySymbol ? currencyFromHook : "";

  const [selectedTimeRange, setSelectedTimeRange] = useState(defaultTimeRange)
  const [totalAmount, setTotalAmount] = useState(0)
  const [adjustedCategories, setAdjustedCategories] = useState<any[]>([])

  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (e.activePayload && e.activePayload.length) {
      // Access coordinates of the active point from the event object
      setTooltipPosition({ x: e.chartX, y: e.chartY - 20 }); // Offset for better placement
    }
  };

  useEffect(() => {
    if (propsTotalAmount) {
      setTotalAmount(propsTotalAmount)
    } else {
      const calculatedTotal = categories.reduce((sum, category) => sum + category.amount, 0)
      setTotalAmount(calculatedTotal)
    }
  }, [categories, propsTotalAmount])

  useEffect(() => {
    if (totalAmount > 0 && categories.length > 0) {
      // Minimum visible percentage (2% se kam ko 2% karenge)
      const MIN_VISIBLE_PERCENTAGE = 2

      const categoriesWithPercentages = categories.map(cat => ({
        ...cat,
        originalAmount: cat.amount,
        originalPercentage: ((cat.amount / totalAmount) * 100).toFixed(1),
        percentage: (cat.amount / totalAmount) * 100
      }))

      // Sort by amount (largest first)
      categoriesWithPercentages.sort((a, b) => b.amount - a.amount)

      // Adjust small slices
      let adjusted = categoriesWithPercentages.map(cat => {
        if (cat.percentage < MIN_VISIBLE_PERCENTAGE) {
          return {
            ...cat,
            amount: (totalAmount * MIN_VISIBLE_PERCENTAGE) / 100,
            isAdjusted: true
          }
        }
        return cat
      })

      setAdjustedCategories(adjusted)
    }
  }, [categories, totalAmount])

  const calculatePercentage = (amount: number): string => {
    if (!totalAmount) return "0.0"
    const rawPercentage = (amount / totalAmount) * 100
    const rounded = rawPercentage.toFixed(1)
    if (amount > 0 && parseFloat(rounded) === 0) {
      return "<0.1"
    }
    return rounded
  }

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    return `${effectiveSymbol}${formatted}`;
  }


  const handleTimeRangeChange = (timeRange: string) => {
    setCat_MonthValue(timeRange)
    setSelectedTimeRange(timeRange)
  }
  
  return (
    <div className="expense-distribution">
      <div className="expense-distribution-header">
        <h2 className="expense-distribution-title">{heading}</h2>
        <TimeRangeDropdown
          timeRanges={timeRanges}
          defaultTimeRange={defaultTimeRange}
          onTimeRangeChange={handleTimeRangeChange}
        />
      </div>

      <div className="expense-distribution-content">
        {isLoading ? (
          <div className="loading-spinner">
            <SimpleSpinner myClassName={"Icon50 mt-5"} />
          </div>
        ) : categories.length > 0 ? (
          <>
            <div className="donut-chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart onMouseMove={handleMouseMove}>
                  <Pie
                  
                    data={adjustedCategories}
                    dataKey="amount"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="65%"
                    outerRadius="90%"
                    paddingAngle={2}
                    minAngle={5}
                  >
                    {adjustedCategories.map((entry, index) => (
                      <Cell
                      
                        key={`cell-${index}`}
                        fill={entry.color}
                        stroke={entry.isAdjusted ? "#fff" : "none"}
                        strokeWidth={entry.isAdjusted ? 1 : 0}
                      />
                    ))}
                  </Pie>

                  <Tooltip content={<CustomTooltip showCurrencySymbol={showCurrencySymbol} effectiveSymbol={effectiveSymbol} />} cursor={{ fill: "transparent" }} position={tooltipPosition} />
                  {showTotalAmountLabel && (
                    <>
                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="total-amount-label"
                      >{totalAmount}
                      </text>
                      <text
                        x="50%"
                        y="62%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="var(--grey-subtext1)"
                        className={TextCss.sub_text_400_12}
                      >
                        Total
                      </text>
                    </>
                  )}

                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="expense-categories">
              {categories.map((category, index) => (
                <div key={index} className="expense-category">
                  <div className="category-label">
                    <span
                      className="category-dot"
                      style={{ backgroundColor: category.color }}
                    ></span>
                    <span className="category-name">{category.name}</span>
                  </div>
                  <div className="category-dashed-line"></div>
                  <div className="category-value">
                    {showTotalAmountLabel ? category.amount : (<>
                      <span className="category-percentage">
                        {calculatePercentage(category.amount)}%
                      </span>
                      <CurrencyTooltip
                        content={category.formatted?.AmountRaw}
                        formatted={category.formatted?.Amount}
                      >
                        <span className="category-amount">
                          ({category.formatted?.Amount ? (showCurrencySymbol ? `[[currency]]${category.formatted.Amount}` : category.formatted.Amount) : (category.formattedAmount ? (showCurrencySymbol ? `[[currency]]${category.formattedAmount}` : category.formattedAmount) : formatCurrency(category.amount))})
                        </span>
                      </CurrencyTooltip>
                    </>)}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            icon={GetImages.emptyStateData}
            className="w-100"
            isBorder={false}
            heading="No Data found"
            isButton={false}
          />
        )}
      </div>
    </div>
  )
}

export default ExpenseDistribution